import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getCustomerCbd,
  getCustomerCbdOrder,
  getSingleOrderCbd
} from '@/api/new-api/cbd/cbd'

// Async Thunks

export const fetchCbdCustomers = createAsyncThunk(
  'cbd/fetchCustomers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { restaurant_id, ...queryParams } = params
      const res = await getCustomerCbd(restaurant_id, queryParams)
      if (res.status === 'success') {
        return res.data
      }
      return rejectWithValue(res.message)
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchCbdCustomerOrders = createAsyncThunk(
  'cbd/fetchCustomerOrders',
  async (
    { customerId, params = {}, restaurant_id } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await getCustomerCbdOrder(restaurant_id, customerId, params)
      if (res.status === 'success') {
        return res.data
      }
      return rejectWithValue(res.message)
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchSingleCbdOrder = createAsyncThunk(
  'cbd/fetchSingleOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const res = await getSingleOrderCbd(orderId)
      if (res.status === 'success') {
        return res.data
      }
      return rejectWithValue(res.message)
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Initial State

const initialState = {
  // Customers List
  customers: [],
  getLoading: false,
  customersError: null,

  // Current Customer Details
  currentCustomer: null,
  customerOrders: [],
  customerStatistics: null,
  getLoading: false,
  ordersError: null,

  // Single Order Details
  singleOrder: null,
  singleOrderLoading: false,
  singleOrderError: null,

  // Pagination
  customersPagination: {
    current_page: 1,
    first_page_url: null,
    from: null,
    last_page: 1,
    last_page_url: null,
    links: {},
    next_page_url: null,
    path: null,
    per_page: 10,
    prev_page_url: null,
    to: null,
    total: 0
  },
  ordersPagination: {
    current_page: 1,
    first_page_url: null,
    from: null,
    last_page: 1,
    last_page_url: null,
    links: {},
    next_page_url: null,
    path: null,
    per_page: 10,
    prev_page_url: null,
    to: null,
    total: 0
  },

  // Messages
  success: null,
  error: null
}

// Slice

const cbdSlice = createSlice({
  name: 'cbd',
  initialState,
  reducers: {
    clearCbdMessages: state => {
      state.success = null
      state.error = null
    },
    setCurrentCustomer: (state, action) => {
      state.currentCustomer = action.payload
    },
    clearOrdersData: state => {
      state.currentCustomer = null
      state.customerOrders = []
      state.customerStatistics = null
      state.ordersError = null
    }
  },
  extraReducers: builder => {
    builder
      // Fetch Customers
      .addCase(fetchCbdCustomers.pending, state => {
        state.getLoading = true
        state.customersError = null
      })
      .addCase(fetchCbdCustomers.fulfilled, (state, action) => {
        state.getLoading = false
        state.customers = action.payload.customers
        state.customersPagination = action.payload.pagination
      })
      .addCase(fetchCbdCustomers.rejected, (state, action) => {
        state.getLoading = false
        state.customersError = action.payload
      })

      // Fetch Customer Orders
      .addCase(fetchCbdCustomerOrders.pending, state => {
        state.getLoading = true
        state.ordersError = null
      })
      .addCase(fetchCbdCustomerOrders.fulfilled, (state, action) => {
        state.getLoading = false
        state.currentCustomer = action.payload.customer
        state.customerOrders = action.payload.orders
        state.customerStatistics = action.payload.statistics
        state.ordersPagination = action.payload.pagination
      })
      .addCase(fetchCbdCustomerOrders.rejected, (state, action) => {
        state.getLoading = false
        state.ordersError = action.payload
      })
      .addCase(fetchSingleCbdOrder.pending, state => {
        state.singleOrderLoading = true
        state.singleOrderError = null
      })
      .addCase(fetchSingleCbdOrder.fulfilled, (state, action) => {
        state.singleOrderLoading = false
        state.singleOrder = action.payload
      })
      .addCase(fetchSingleCbdOrder.rejected, (state, action) => {
        state.singleOrderLoading = false
        state.singleOrderError = action.payload
      })
  }
})

export const { clearCbdMessages, setCurrentCustomer, clearOrdersData } =
  cbdSlice.actions
export default cbdSlice.reducer
